import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

export interface PriceUpdate {
  symbol: string;
  price: number;
  timestamp: string;
  volume24h?: number;
  marketCap?: number;
}

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected' | 'idle';

interface PriceStreamState {
  prices: Record<string, PriceUpdate>;
  connectionStatus: ConnectionStatus;
  lastUpdated: number | null;
}

const initialState: PriceStreamState = {
  prices: {},
  connectionStatus: 'idle',
  lastUpdated: null,
};

const priceStreamSlice = createSlice({
  name: 'priceStream',
  initialState,
  reducers: {
    setPrices(state, action: PayloadAction<PriceUpdate[]>) {
      action.payload.forEach((update) => {
        state.prices[update.symbol] = update;
      });
      state.lastUpdated = Date.now();
    },
    priceUpdated(state, action: PayloadAction<PriceUpdate>) {
      state.prices[action.payload.symbol] = action.payload;
      state.lastUpdated = Date.now();
    },
    setConnectionStatus(state, action: PayloadAction<ConnectionStatus>) {
      state.connectionStatus = action.payload;
    },
  },
});

export const { setPrices, priceUpdated, setConnectionStatus } = priceStreamSlice.actions;

// Selectors
export const selectPrices = (state: RootState): Record<string, PriceUpdate> =>
  state.priceStream.prices;

export const selectConnectionStatus = (state: RootState): ConnectionStatus =>
  state.priceStream.connectionStatus;

export const selectLastUpdated = (state: RootState): number | null =>
  state.priceStream.lastUpdated;

export const selectPriceBySymbol = (symbol: string) =>
  createSelector(selectPrices, (prices): PriceUpdate | undefined => prices[symbol]);

export default priceStreamSlice.reducer;
